import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card } from 'react-bootstrap';

export default function ProjectMetricsChart({ logs, blueprint }) {
    // 1. Isolate only the numerical fields from the blueprint
    const numericFields = blueprint.filter(f => f.data_type === 'number');

    // If the user hasn't defined any number fields, don't render the chart box at all
    if (numericFields.length === 0 || logs.length === 0) return null;

    // 2. Process logs into chronological order and extract the metrics
    const chartData = logs
        .map(log => {
            const dataPoint = {
                rawDate: new Date(log.timestamp),
                displayDate: new Date(log.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })
            };

            let hasNumericData = false;

            // Dig into the log's JSON payload and extract the numbers
            numericFields.forEach(field => {
                const val = log.metrics?.[field.field_name];
                if (val !== undefined && val !== null && val !== '') {
                    dataPoint[field.field_name] = parseFloat(val);
                    hasNumericData = true;
                }
            });

            return hasNumericData ? dataPoint : null;
        })
        .filter(Boolean) // Drop any logs that didn't contain numerical data
        .sort((a, b) => a.rawDate - b.rawDate); // Sort oldest to newest

    // If logs exist, but none of them contain numerical data yet, hide the chart
    if (chartData.length === 0) return null;

    // A clean color palette for the lines
    const colors = ['#0d6efd', '#20c997', '#ffc107', '#fd7e14', '#6f42c1', '#d63384'];

    return (
        <Card className="border-0 shadow-sm rounded-4 mb-4 fade-in">
            <Card.Body className="p-4">
                <h6 className="fw-bold text-uppercase letter-spacing-1 text-muted mb-4">
                    <i className="bi bi-graph-up-arrow me-2 text-primary"></i>Metrics Over Time
                </h6>
                <div style={{ height: '280px', width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
                            <XAxis dataKey="displayDate" tick={{ fontSize: 12, fill: '#6c757d' }} axisLine={false} tickLine={false} dy={10} />
                            <YAxis tick={{ fontSize: 12, fill: '#6c757d' }} axisLine={false} tickLine={false} dx={-10} />
                            <Tooltip
                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                labelStyle={{ fontWeight: 'bold', color: '#495057', marginBottom: '5px' }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '15px', fontSize: '13px', fontWeight: '500' }} iconType="circle" />

                            {numericFields.map((field, idx) => (
                                <Line
                                    key={field.id}
                                    type="monotone"
                                    dataKey={field.field_name}
                                    // Append the unit to the legend name if one exists (e.g., "Height (cm)")
                                    name={field.options?.[0] ? `${field.field_name} (${field.options[0]})` : field.field_name}
                                    stroke={colors[idx % colors.length]}
                                    strokeWidth={3}
                                    dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                    connectNulls={true} // Crucial: draws the line across days where a specific metric wasn't logged
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card.Body>
        </Card>
    );
}
